import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { api } from "@/modules/api";
import { getBackendApiErrorMessage } from "@/modules/api/errorHandling";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { EmailSenderObservable } from "@/store/email-senders/EmailSenderObservable";
import {
  EmailIntegrationFormattingMode,
  EmailIntegrationFormattingInstructions,
  EmailIntegrationOrganizingMode,
  EmailIntegrationOrganizingInstructions,
  IEmailSenderStore,
  EmailIntegrationSettings,
  EmailIntegrationOrganizingManualCollections,
} from "@/store/email-senders/types";
import { toastModule } from "@/modules/toast";
import { AppStore } from "@/store";
import { VerificationStatus } from "@/domains/integrations/verification-status";
import { trackEvent, TrackedEvent } from "@/domains/metrics";

export class AppStoreEmailSendersStore implements IEmailSenderStore {
  private data: EmailSenderObservable[] = [];
  private loading = false;
  private loadingSettings = false;
  private savingSettings = false;
  private creating = false;
  private deleting = false;
  private verifying = false;
  private requestingNewVerificationCode = false;
  private _formattingMode: EmailIntegrationFormattingMode = "smart";
  private _formattingInstructions: EmailIntegrationFormattingInstructions = null;
  private _organizingOption: EmailIntegrationOrganizingMode[] = [];
  private _organizingInstructions: EmailIntegrationOrganizingInstructions = null;
  private _organizingManualCollections: EmailIntegrationOrganizingManualCollections = [];

  private static CLIENT_ONLY_ID = "client-only-id";

  public static VERIFICATION_TIMEOUT = 60_000;

  constructor(private readonly store: AppStore) {
    makeObservable<
      AppStoreEmailSendersStore,
      | "data"
      | "loading"
      | "loadingSettings"
      | "savingSettings"
      | "creating"
      | "deleting"
      | "verifying"
      | "requestingNewVerificationCode"
      | "_formattingMode"
      | "_formattingInstructions"
      | "_organizingOption"
      | "_organizingInstructions"
      | "resetSettingFromResponse"
      | "_organizingManualCollections"
    >(this, {
      data: observable,
      items: computed,
      unVerifiedItem: computed,
      addUnverifiedItem: action,
      clearUnverifiedItem: action,
      verifiedItems: computed,
      // Load
      load: action,
      loading: observable,
      isLoading: computed,

      // Create
      create: action,
      creating: observable,
      isCreating: computed,

      // Verify
      verify: action,
      verifying: observable,
      isVerifying: computed,

      // Delete
      delete: action,
      deleting: observable,
      isDeleting: computed,

      // Request new verification code
      requestNewVerificationCode: action,
      requestingNewVerificationCode: observable,
      isRequestingNewVerificationCode: computed,

      // New formatting observables
      _formattingMode: observable,
      _formattingInstructions: observable,
      _organizingOption: observable,
      _organizingInstructions: observable,

      formattingMode: computed,
      formattingInstructions: computed,
      organizingOption: computed,
      organizingInstructions: computed,

      setFormattingOption: action,
      setFormattingInstructions: action,
      setOrganizingOption: action,
      setOrganizingInstructions: action,

      loadingSettings: observable,
      savingSettings: observable,
      isLoadingSettings: computed,
      isSavingSettings: computed,
      loadSettings: action,
      saveSettings: action,
      resetSettingFromResponse: action,

      _organizingManualCollections: observable,
      organizingManualCollections: computed,
      setOrganizingManualCollections: action,
    });
  }

  get isLoading() {
    return this.loading;
  }

  get items() {
    return this.data;
  }

  get verifiedItems() {
    return this.data.filter(item => item.isVerified);
  }

  get isCreating() {
    return this.creating;
  }

  get isVerifying() {
    return this.verifying;
  }

  get isDeleting() {
    return this.deleting;
  }

  get unVerifiedItem() {
    return this.data.find(item => !item.isVerified);
  }

  get isRequestingNewVerificationCode() {
    return this.requestingNewVerificationCode;
  }

  get formattingMode() {
    return this._formattingMode;
  }

  get formattingInstructions() {
    return this._formattingInstructions;
  }

  get organizingOption() {
    return this._organizingOption;
  }

  get organizingInstructions() {
    return this._organizingInstructions;
  }

  get isLoadingSettings() {
    return this.loadingSettings;
  }

  get isSavingSettings() {
    return this.savingSettings;
  }

  get organizingManualCollections() {
    return this._organizingManualCollections;
  }

  load = async () => {
    runInAction(() => {
      this.loading = true;
    });

    try {
      const response = await api.get("/v2/integrations/emails/senders", {});

      runInAction(() => {
        const myPersonalSpaceAccount = this.store.spaceAccounts.myPersonalSpaceAccount;
        const myEmailSender = new EmailSenderObservable({
          id: "-",
          email: myPersonalSpaceAccount?.profileEmailAddress ?? "",
          is_verified: true,
          space_account_id: myPersonalSpaceAccount?.id ?? "",
          can_delete: false,
        });

        this.data = [
          myEmailSender,
          ...(response.data?.senders.map(sender => {
            const emailSender = new EmailSenderObservable(sender);
            if (!sender.is_verified) {
              emailSender.setVerificationStatus(VerificationStatus.AwaitsVerification);
            }
            return emailSender;
          }) ?? [myEmailSender]),
        ];
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "Failed to load email senders",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  create = async (email: string) => {
    runInAction(() => {
      this.creating = true;
    });

    try {
      const response = await api.post("/v2/integrations/emails/senders", {
        body: {
          email,
        },
      });

      const data = response.data;
      if (!data || response.error) {
        throw new Error(getBackendApiErrorMessage(response.error) ?? "Failed to add email");
      }

      runInAction(() => {
        const unVerifiedItem = this.unVerifiedItem; // TODO: BE removes dots from email, this should compare emails
        if (unVerifiedItem) {
          unVerifiedItem.reset(data);
          unVerifiedItem.setVerificationStatus(VerificationStatus.AwaitsVerification);
        } else {
          this.data.push(new EmailSenderObservable(data));
        }
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreEmailSendersStore] Failed to create email sender",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: error.message,
      });

      throw error;
    } finally {
      runInAction(() => {
        this.creating = false;
      });
    }
  };

  delete = async (id: string): Promise<void> => {
    const sender = this.data.find(sender => sender.id === id);

    runInAction(() => {
      this.deleting = true;
      this.data = this.data.filter(sender => sender.id !== id);
    });

    try {
      const response = await api.delete("/v2/integrations/emails/senders/{sender_id}", {
        params: { path: { sender_id: id } },
      });

      const data = response.data;
      if (!data || response.error) {
        throw new Error(getBackendApiErrorMessage(response.error) ?? "Failed to delete email");
      }

      trackEvent(TrackedEvent.EmailDisconnect, { domain: sender?.emailDomain });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreEmailSendersStore] Failed to delete email sender",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: error.message,
      });

      throw error;
    } finally {
      runInAction(() => {
        this.deleting = false;
      });
    }
  };

  verify = async (id: string, verificationCode: string) => {
    const sender = this.data.find(sender => sender.id === id);

    runInAction(() => {
      this.verifying = true;
    });

    try {
      const response = await api.post("/v2/integrations/emails/senders/{sender_id}/verify", {
        params: { path: { sender_id: id } },
        body: {
          token: verificationCode,
        },
      });

      const data = response.data;
      if (!data || response.error) {
        throw new Error(getBackendApiErrorMessage(response.error) ?? "Failed to verify email");
      }

      runInAction(() => {
        const item = this.data.find(item => item.id === id);
        item?.setVerificationStatus(VerificationStatus.Verified);
      });

      trackEvent(TrackedEvent.EmailConnect, { domain: sender?.emailDomain });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreEmailSendersStore] Failed to verify email sender",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      runInAction(() => {
        const item = this.data.find(item => item.id === id);
        if (item) {
          item.setVerificationStatus(VerificationStatus.Invalid);
        } else {
          toastModule.triggerToast({
            content: error.message,
          });
        }
      });
    } finally {
      runInAction(() => {
        this.verifying = false;
      });
    }
  };

  requestNewVerificationCode = async (id: string) => {
    runInAction(() => {
      this.requestingNewVerificationCode = true;
      this.unVerifiedItem?.setVerificationToken("");
    });

    try {
      const response = await api.post("/v2/integrations/emails/senders/{sender_id}/request", {
        params: { path: { sender_id: id } },
      });

      const data = response.data;
      if (!data || response.error) {
        throw new Error(
          getBackendApiErrorMessage(response.error) ?? "Failed to resend verification code"
        );
      }
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreEmailSendersStore] Failed to request new verification code",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: error.message,
      });
    } finally {
      runInAction(() => {
        this.requestingNewVerificationCode = false;
      });
    }
  };

  addUnverifiedItem = () => {
    this.data.push(
      new EmailSenderObservable({
        id: AppStoreEmailSendersStore.CLIENT_ONLY_ID,
        email: "",
        is_verified: false,
        space_account_id: "",
      })
    );
  };

  clearUnverifiedItem = async () => {
    // if item made it to BE it should have an id
    // if so, we can proceed with deleting it
    if (
      this.unVerifiedItem &&
      this.unVerifiedItem.id !== AppStoreEmailSendersStore.CLIENT_ONLY_ID
    ) {
      await this.delete(this.unVerifiedItem.id);
      return;
    }

    // if item is not in BE, we can simply remove it from the list
    this.data = this.data.filter(sender => sender.id !== this.unVerifiedItem?.id);
  };

  loadSettings = async () => {
    if (this.loadingSettings) {
      return;
    }

    runInAction(() => {
      this.loadingSettings = true;
    });

    try {
      const response = await api.get("/v2/integrations/emails/settings", {});
      const settings = response.data;

      if (settings) {
        runInAction(() => {
          this.resetSettingFromResponse(settings);
        });
      }
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "Failed to load email settings",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: "Failed to load email settings",
      });
    } finally {
      runInAction(() => {
        this.loadingSettings = false;
      });
    }
  };

  saveSettings = async () => {
    runInAction(() => {
      this.savingSettings = true;
    });

    try {
      const response = await api.post("/v2/integrations/emails/settings", {
        body: {
          formatting_mode: this._formattingMode as "simple" | "smart",
          formatting_instructions: this._formattingInstructions ?? "",
          organizing_manual_enabled: this._organizingOption.includes("manual"),
          organizing_smart_enabled: this._organizingOption.includes("smart"),
          organizing_instructions: this._organizingInstructions ?? "",
          organizing_manual_collections: this.organizingManualCollections,
        },
      });

      const data = response.data;
      if (!data || response.error) {
        throw new Error(
          getBackendApiErrorMessage(response.error) ?? "Failed to save email settings"
        );
      }

      runInAction(() => {
        this.resetSettingFromResponse(data);
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreEmailSendersStore] Failed to save email settings",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: error.message,
      });
      throw error;
    } finally {
      runInAction(() => {
        this.savingSettings = false;
      });
    }
  };

  setFormattingOption = async (value: EmailIntegrationFormattingMode) => {
    runInAction(() => {
      this._formattingMode = value;
      if (value !== "smart") {
        this._formattingInstructions = null;
      }
    });
    await this.saveSettings();
    trackEvent(TrackedEvent.EmailFormattingModeSelect, { mode: value });
  };

  setFormattingInstructions = async (value: EmailIntegrationFormattingInstructions) => {
    runInAction(() => {
      this._formattingInstructions = value;
      if (value !== null) {
        this._formattingMode = "smart";
      }
    });
    await this.saveSettings();
    trackEvent(TrackedEvent.EmailFormattingSmartModeCustomize, { instructions: value });
  };

  setOrganizingOption = async (value: EmailIntegrationOrganizingMode[]) => {
    const prevValue = [...this._organizingOption];

    runInAction(() => {
      this._organizingOption = value;
      if (!value.includes("smart")) {
        this._organizingInstructions = null;
      }

      if (!value.includes("manual")) {
        this._organizingManualCollections = [];
      }
    });
    await this.saveSettings();

    const modes: EmailIntegrationOrganizingMode[] = ["smart", "manual"];
    for (const mode of modes) {
      if (prevValue.includes(mode) && !value.includes(mode)) {
        trackEvent(TrackedEvent.EmailOrganizingModeUnchecked, { mode });
      }
      if (!prevValue.includes(mode) && value.includes(mode)) {
        trackEvent(TrackedEvent.EmailOrganizingModeChecked, { mode });
      }
    }
  };

  setOrganizingInstructions = async (value: EmailIntegrationOrganizingInstructions) => {
    runInAction(() => {
      this._organizingInstructions = value;
      if (value !== null) {
        this._organizingOption.push("smart");
      }
    });

    await this.saveSettings();
    trackEvent(TrackedEvent.EmailOrganizingSmartModeCustomize, { instructions: value });
  };

  setOrganizingManualCollections = async (value: EmailIntegrationOrganizingManualCollections) => {
    runInAction(() => {
      this._organizingManualCollections = value;
    });

    await this.saveSettings();

    const collections = await this.store.collections.getManyAsync(value || []);
    trackEvent(TrackedEvent.EmailOrganizingSpecificCollectionsSaved, {
      num_collections: collections.length,
      collection_titles: collections.map(collection => collection.title),
    });
  };

  private resetSettingFromResponse = (settings: EmailIntegrationSettings) => {
    this._formattingMode = settings.formatting_mode;
    this._formattingInstructions = settings.formatting_instructions || null;

    this._organizingOption = [];
    if (settings.organizing_smart_enabled) this._organizingOption.push("smart");
    if (settings.organizing_manual_enabled) this._organizingOption.push("manual");

    this._organizingInstructions = settings.organizing_instructions || null;
    this._organizingManualCollections = settings.organizing_manual_collections;
  };
}
